// client/src/components/profissionais/tabs/UserServicesTab.js
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useUserServicosByUser } from '../../../hooks/useRelationships';
import { useServicosPorConta } from '../../../hooks/useServicos';
import { useTenantI18n } from '../../../hooks/useAccounts';
import { getCurrencySymbol } from '../../../utils/formatters';
import { 
    ChevronDown, ChevronUp, DollarSign, MapPin, RefreshCw, Search, Loader2, Plus, CheckCircle
} from 'lucide-react';
import DesvincularServicoModal from './DesvincularServicoModal';

const extractId = (obj) => {
    if (!obj) return null;
    if (typeof obj === 'string') return obj;
    return String(obj._id || obj.id || obj);
};

const UserServicesTab = ({ 
    userId, 
    contaId, 
    selectedServicos, 
    onServicosChange, 
    isProfissional, 
    comissoesConfig,
    unidadesConfig,
    unidadesDisponiveis,
    isEditing = false,
    userNome,
    refetchVinculos
}) => {
    // ✅ ACHADO: rótulos abaixo eram fixos em "(R$)", sem ler
    // configuracoesGerais.i18n da conta.
    const { currency, locale } = useTenantI18n();
    const currencySymbol = getCurrencySymbol(currency, locale);

    const { data: servicosData, isLoading: loadingServicos } = useServicosPorConta(contaId, {
        ativo: true,
        agendavel: true,
        limit: 1000
    });
    
    const { data: userServicosData, refetch: refetchUserServicos, isLoading: loadingUserServicos } = useUserServicosByUser(userId, contaId, {
        enabled: !!userId && !!contaId
    });
    
    const servicosDaConta = useMemo(() => servicosData?.data || [], [servicosData]);
    const userServicosVinculos = useMemo(() => (userServicosData?.data || []).filter(v => v.status === 'ativo'), [userServicosData]);
    
    const [comissoesLocais, setComissoesLocais] = useState({});
    const [unidadesLocais, setUnidadesLocais] = useState({});
    const [servicosSelecionados, setServicosSelecionados] = useState([]);
    const [expandedServices, setExpandedServices] = useState({}); 
    const [searchTerm, setSearchTerm] = useState('');
    
    const [servicoParaDesvincular, setServicoParaDesvincular] = useState(null);
    const [showDesvincularModal, setShowDesvincularModal] = useState(false);

    const [dadosCarregados, setDadosCarregados] = useState(false);
    const initialSyncDone = useRef(false);

    // ✅ Sincronização inicial blindada sem loop infinito
    useEffect(() => {
        if (loadingServicos || loadingUserServicos || initialSyncDone.current) return;
        if (!userServicosData) return; 

        let idsSelecionados = [];
        let configComissoes = {};
        let configUnidades = {};

        if (userServicosVinculos.length > 0) {
            idsSelecionados = userServicosVinculos.map(v => extractId(v.servico)).filter(Boolean);
            
            userServicosVinculos.forEach(v => {
                const sId = extractId(v.servico);
                if (sId) {
                    if (v.comissao) configComissoes[sId] = { ...v.comissao };
                    if (v.unidades) configUnidades[sId] = v.unidades.map(u => extractId(u));
                }
            });
        } else if (selectedServicos && selectedServicos.length > 0) {
            idsSelecionados = [...selectedServicos];
            configComissoes = comissoesConfig ? { ...comissoesConfig } : {};
            configUnidades = unidadesConfig ? { ...unidadesConfig } : {};
        }

        setServicosSelecionados(idsSelecionados);
        setComissoesLocais(configComissoes);
        setUnidadesLocais(configUnidades);
        
        initialSyncDone.current = true;
        setDadosCarregados(true);
        
    }, [userServicosData, userServicosVinculos, loadingServicos, loadingUserServicos]); 

    const vinculosMap = useMemo(() => {
        const map = new Map();
        userServicosVinculos.forEach(v => {
            const servicoId = extractId(v.servico);
            if (servicoId) map.set(servicoId, v);
        });
        return map;
    }, [userServicosVinculos]);

    const servicosRenderizaveis = useMemo(() => {
        if (!dadosCarregados) return [];

        const mapServicos = new Map();
        servicosDaConta.forEach(s => mapServicos.set(extractId(s), s));
        
        userServicosVinculos.forEach(v => {
            if (v.servico && typeof v.servico === 'object') {
                const sId = extractId(v.servico);
                if (!mapServicos.has(sId)) mapServicos.set(sId, v.servico);
            }
        });

        let arrayFinal = Array.from(mapServicos.values()).map(servico => {
            const servicoId = extractId(servico);
            const isSelected = servicosSelecionados.includes(servicoId);
            const vinculoNoBanco = vinculosMap.get(servicoId);
            const hasVinculoNoBanco = !!vinculoNoBanco;

            let comissaoConfig = comissoesLocais[servicoId] || vinculoNoBanco?.comissao || {
                ativo: false,
                tipoAplicacao: 'sobre_servico',
                faixas: [{ type: 'percentual', valor: 0, limite: { type: 'sem_limite', valor: 0 } }],
                considerarTaxas: false,
                considerarDescontos: true
            };

            let unidadesConfigServico = unidadesLocais[servicoId] || [];

            return {
                ...servico,
                _id: servicoId,
                isSelected,
                hasVinculoNoBanco,
                vinculoNoBanco,
                comissao: comissaoConfig,
                unidadesMarcadas: unidadesConfigServico
            };
        });

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            arrayFinal = arrayFinal.filter(s => (s.nome && s.nome.toLowerCase().includes(term)));
        }

        return arrayFinal;
    }, [servicosDaConta, userServicosVinculos, servicosSelecionados, vinculosMap, comissoesLocais, unidadesLocais, searchTerm, dadosCarregados]);

    const toggleServiceExpansion = (servicoId) => {
        const idStr = extractId(servicoId);
        setExpandedServices(prev => ({
            ...prev,
            [idStr]: !prev[idStr]
        }));
    };

    const handleServicoToggle = (servicoId) => {
        if (!isEditing) return;
        
        const idStr = extractId(servicoId);
        const isSelected = servicosSelecionados.includes(idStr);
        const vinculoNoBanco = vinculosMap.get(idStr);
        
        if (isSelected) {
            if (vinculoNoBanco) {
                setServicoParaDesvincular(vinculoNoBanco);
                setShowDesvincularModal(true);
            } else {
                const novosSelecionados = servicosSelecionados.filter(id => id !== idStr);
                setServicosSelecionados(novosSelecionados);
                if (onServicosChange) onServicosChange(novosSelecionados, comissoesLocais, unidadesLocais);
            }
        } else {
            const novosSelecionados = [...servicosSelecionados, idStr];
            let novasComissoes = { ...comissoesLocais };
            let novasUnidades = { ...unidadesLocais };

            if (!novasComissoes[idStr]) {
                 novasComissoes[idStr] = {
                    ativo: false,
                    tipoAplicacao: 'sobre_servico',
                    faixas: [{ type: 'percentual', valor: 0, limite: { type: 'sem_limite', valor: 0 } }],
                    considerarTaxas: false,
                    considerarDescontos: true
                };
            }

            if (!novasUnidades[idStr] || novasUnidades[idStr].length === 0) {
                novasUnidades[idStr] = (unidadesDisponiveis || []).map(u => extractId(u));
            }
            
            setServicosSelecionados(novosSelecionados);
            setComissoesLocais(novasComissoes);
            setUnidadesLocais(novasUnidades);
            setExpandedServices(prev => ({ ...prev, [idStr]: true }));

            if (onServicosChange) onServicosChange(novosSelecionados, novasComissoes, novasUnidades);
        }
    };

    const handleUnidadeToggle = (servicoId, unidadeIdStr) => {
        if (!isEditing) return;
        setUnidadesLocais(prev => {
            const idSrv = extractId(servicoId);
            const atuais = prev[idSrv] || [];
            const novoArray = atuais.includes(unidadeIdStr) 
                ? atuais.filter(id => id !== unidadeIdStr) 
                : [...atuais, unidadeIdStr];

            const novasUnidades = { ...prev, [idSrv]: novoArray };
            if (onServicosChange) onServicosChange(servicosSelecionados, comissoesLocais, novasUnidades);
            return novasUnidades;
        });
    };

    const handleComissaoChange = (servicoId, field, value) => {
        if (!isEditing) return;
        setComissoesLocais(prev => {
            const idSrv = extractId(servicoId);
            const atual = prev[idSrv] || { ativo: false, faixas: [], tipoAplicacao: 'sobre_servico' };
            const novasComissoes = { ...prev, [idSrv]: { ...atual, [field]: value } };
            if (onServicosChange) onServicosChange(servicosSelecionados, novasComissoes, unidadesLocais);
            return novasComissoes;
        });
    };

    const handleFaixaChange = (servicoId, index, field, value) => {
        if (!isEditing) return;
        setComissoesLocais(prev => {
            const idSrv = extractId(servicoId);
            const atual = prev[idSrv] || { ativo: false, faixas: [] };
            const faixas = [...(atual.faixas || [])];
            if (!faixas[index]) faixas[index] = { type: 'percentual', valor: 0, limite: { type: 'sem_limite', valor: 0 } };

            if (field.includes('.')) {
                const [parent, child] = field.split('.');
                const parentObj = faixas[index][parent] || {};
                faixas[index] = { ...faixas[index], [parent]: { ...parentObj, [child]: value } };
            } else {
                faixas[index] = { ...faixas[index], [field]: value };
            }

            const novasComissoes = { ...prev, [idSrv]: { ...atual, faixas } };
            if (onServicosChange) onServicosChange(servicosSelecionados, novasComissoes, unidadesLocais);
            return novasComissoes;
        });
    };

    const handleAddFaixa = (servicoId) => {
        if (!isEditing) return;
        setComissoesLocais(prev => {
            const idSrv = extractId(servicoId);
            const atual = prev[idSrv] || { ativo: false, faixas: [] };
            const faixas = [...(atual.faixas || []), { type: 'percentual', valor: 0, limite: { type: 'sem_limite', valor: 0 } }];
            const novasComissoes = { ...prev, [idSrv]: { ...atual, faixas } };
            if (onServicosChange) onServicosChange(servicosSelecionados, novasComissoes, unidadesLocais);
            return novasComissoes;
        });
    };

    const handleRemoveFaixa = (servicoId, index) => {
        if (!isEditing) return;
        setComissoesLocais(prev => {
            const idSrv = extractId(servicoId);
            const atual = prev[idSrv];
            if (!atual || !atual.faixas) return prev;
            const faixas = atual.faixas.filter((_, idx) => idx !== index);
            const novasComissoes = { ...prev, [idSrv]: { ...atual, faixas } };
            if (onServicosChange) onServicosChange(servicosSelecionados, novasComissoes, unidadesLocais);
            return novasComissoes;
        });
    };

    const handleDesvincularSuccess = () => {
        if(refetchUserServicos) refetchUserServicos();
        if(refetchVinculos) refetchVinculos(); 
        
        if (servicoParaDesvincular) {
             const idStr = extractId(servicoParaDesvincular.servico);
             const novosSelecionados = servicosSelecionados.filter(id => id !== idStr);
             setServicosSelecionados(novosSelecionados);
             if (onServicosChange) onServicosChange(novosSelecionados, comissoesLocais, unidadesLocais);
        }

        setShowDesvincularModal(false);
        setServicoParaDesvincular(null);
    };

    const handleReload = () => {
        if(refetchUserServicos) refetchUserServicos();
        if(refetchVinculos) refetchVinculos();
        initialSyncDone.current = false;
        setDadosCarregados(false);
    };

    if (!isProfissional) return null;

    if (loadingServicos || loadingUserServicos || !dadosCarregados) {
        return (
            <div className="flex justify-center items-center py-12">
                <Loader2 size={32} className="animate-spin text-blue-600" />
            </div>
        );
    }

    const renderServicoRow = (servico) => {
        const servicoId = extractId(servico);
        const isExpanded = expandedServices[servicoId];
        
        return (
            <div key={servicoId} className="border-b border-gray-100 last:border-b-0 bg-white">
                <div className={`flex items-center justify-between p-4 transition-colors ${!isEditing ? 'opacity-70' : 'hover:bg-gray-50'}`}>
                    
                    <div className="flex items-center gap-4 flex-1">
                        <button 
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleServicoToggle(servicoId); }}
                            disabled={!isEditing}
                            className={`relative inline-flex items-center h-[22px] w-10 rounded-full transition-colors focus:outline-none flex-shrink-0 ${servico.isSelected ? 'bg-amber-400' : 'bg-gray-200'}`}
                        >
                            <span className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform shadow-sm ${servico.isSelected ? 'translate-x-[22px]' : 'translate-x-1'}`} />
                        </button>
                        
                        <div className="flex-1 min-w-0">
                            <span className="text-[15px] font-medium text-gray-800 block truncate">{servico.nome}</span>
                        </div>
                    </div>
                    
                    <div 
                        className={`flex items-center gap-3 ${servico.isSelected && isEditing ? 'cursor-pointer group' : ''}`}
                        onClick={() => { if(isEditing && servico.isSelected) toggleServiceExpansion(servicoId) }}
                    >
                        <span className="text-xs text-gray-400 hidden sm:block">
                            {servico.comissao?.ativo ? 'Comissão definida' : 'Comissão não definida'}
                        </span>
                        {servico.isSelected && (
                            <ChevronDown size={18} className={`text-gray-400 transition-transform group-hover:text-gray-600 ${isExpanded ? 'rotate-180' : ''}`} />
                        )}
                    </div>
                </div>

                {isExpanded && servico.isSelected && (
                    <div className="p-5 bg-gray-50/50 border-t border-gray-100">
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                            
                            <div>
                                <h5 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                                    <MapPin size={14} className="mr-1 text-gray-500" />
                                    Em quais unidades ele faz este serviço?
                                </h5>
                                
                                {(!unidadesDisponiveis || unidadesDisponiveis.length === 0) ? (
                                    <div className="bg-yellow-50 text-yellow-800 p-3 rounded text-sm border border-yellow-200">
                                        Este profissional ainda não foi vinculado a nenhuma unidade na aba "Unidades".
                                    </div>
                                ) : (
                                    <div className="bg-white border border-gray-200 rounded overflow-hidden shadow-sm">
                                        <div className="max-h-64 overflow-y-auto divide-y divide-gray-100">
                                            {unidadesDisponiveis.map(u => {
                                                const unidIdStr = extractId(u);
                                                const unidName = typeof u === 'object' ? u.nome : `Unidade ID: ${unidIdStr.substring(0, 6)}...`;
                                                const isUnidadeMarcada = servico.unidadesMarcadas?.includes(unidIdStr);

                                                return (
                                                    <label key={unidIdStr} className={`flex items-center p-3 cursor-pointer transition-colors hover:bg-gray-50 ${isUnidadeMarcada ? 'bg-blue-50/50' : ''}`}>
                                                        <input 
                                                            type="checkbox"
                                                            className="form-checkbox mr-3"
                                                            checked={isUnidadeMarcada}
                                                            onChange={() => handleUnidadeToggle(servicoId, unidIdStr)}
                                                            disabled={!isEditing}
                                                        />
                                                        <p className="text-sm font-medium text-gray-800">{unidName}</p>
                                                    </label>
                                                )
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div>
                                <h5 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                                    <DollarSign size={14} className="mr-1 text-gray-500" />
                                    Configuração de Comissão
                                </h5>
                                
                                <div className="flex items-center gap-2 mb-4">
                                    <label className="checkbox-label text-sm flex items-center cursor-pointer">
                                        <div className={`relative inline-flex items-center h-[22px] w-10 rounded-full transition-colors mr-3 ${servico.comissao.ativo ? 'bg-amber-400' : 'bg-gray-300'}`}>
                                            <span className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform shadow-sm ${servico.comissao.ativo ? 'translate-x-[22px]' : 'translate-x-1'}`} />
                                        </div>
                                        <input 
                                            type="checkbox" 
                                            checked={servico.comissao.ativo || false} 
                                            onChange={(e) => handleComissaoChange(servicoId, 'ativo', e.target.checked)} 
                                            className="hidden"
                                            disabled={!isEditing}
                                        />
                                        <span className="font-medium text-gray-800">Ativar Comissão Personalizada</span>
                                    </label>
                                </div>

                                {servico.comissao.ativo && (
                                    <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm max-w-xl">
                                        <div className="mb-4">
                                            <label className="text-xs font-medium text-gray-600 block mb-1">Incidir Sobre</label>
                                            <select 
                                                value={servico.comissao.tipoAplicacao || 'sobre_servico'} 
                                                onChange={(e) => handleComissaoChange(servicoId, 'tipoAplicacao', e.target.value)} 
                                                disabled={!isEditing}
                                                className="form-input text-sm w-full md:w-1/2 bg-white"
                                            >
                                                <option value="sobre_servico">Valor do Serviço</option>
                                                <option value="sobre_lucro">Lucro Líquido</option>
                                                <option value="sobre_valor_total">Valor Total da Venda</option>
                                            </select>
                                        </div>
                                        
                                        <div className="space-y-3">
                                            <label className="text-xs font-medium text-gray-600 block mb-2">Faixas de Comissão</label>
                                            {servico.comissao.faixas?.map((faixa, idx) => (
                                                <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-2">
                                                    <div className="grid grid-cols-12 gap-3 items-center mb-3">
                                                        <div className="col-span-12 md:col-span-4">
                                                            <label className="text-[11px] text-gray-500 uppercase tracking-wider">Tipo</label>
                                                            <select
                                                                value={faixa.tipo || faixa.type || 'percentual'}
                                                                onChange={(e) => handleFaixaChange(servicoId, idx, 'tipo', e.target.value)}
                                                                disabled={!isEditing}
                                                                className="form-input text-sm w-full mt-1 bg-white"
                                                            >
                                                                <option value="percentual">Porcentagem (%)</option>
                                                                <option value="valor_fixo">Valor Fixo ({currencySymbol})</option>
                                                            </select>
                                                        </div>
                                                        <div className="col-span-12 md:col-span-8">
                                                            <label className="text-[11px] text-gray-500 uppercase tracking-wider">Valor</label>
                                                            <input
                                                                type="number"
                                                                value={faixa.valor || 0}
                                                                onChange={(e) => handleFaixaChange(servicoId, idx, 'valor', parseFloat(e.target.value) || 0)}
                                                                disabled={!isEditing}
                                                                className="form-input text-sm w-full mt-1"
                                                                min="0"
                                                                step={(faixa.tipo || faixa.type) === 'percentual' ? '0.1' : '0.01'}
                                                            />
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="grid grid-cols-12 gap-3 items-center">
                                                        <div className="col-span-12 md:col-span-4">
                                                            <label className="text-[11px] text-gray-500 uppercase tracking-wider">Limite</label>
                                                            <select
                                                                value={faixa.limite?.tipo || faixa.limite?.type || 'sem_limite'}
                                                                onChange={(e) => handleFaixaChange(servicoId, idx, 'limite.tipo', e.target.value)}
                                                                disabled={!isEditing}
                                                                className="form-input text-sm w-full mt-1 bg-white"
                                                            >
                                                                <option value="sem_limite">Sem Limite</option>
                                                                <option value="valor_maximo">Máx ({currencySymbol})</option>
                                                            </select>
                                                        </div>
                                                        <div className="col-span-12 md:col-span-8">
                                                            {(faixa.limite?.tipo === 'valor_maximo' || faixa.limite?.type === 'valor_maximo') && (
                                                                <>
                                                                    <label className="text-[11px] text-gray-500 uppercase tracking-wider">Valor Máximo ({currencySymbol})</label>
                                                                    <input
                                                                        type="number"
                                                                        value={faixa.limite?.valor || 0}
                                                                        onChange={(e) => handleFaixaChange(servicoId, idx, 'limite.valor', parseFloat(e.target.value) || 0)}
                                                                        disabled={!isEditing}
                                                                        className="form-input text-sm w-full mt-1"
                                                                        min="0"
                                                                        step="0.01"
                                                                    />
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                    
                                                    {servico.comissao.faixas.length > 1 && isEditing && (
                                                        <div className="mt-3 flex justify-end">
                                                            <button
                                                                type="button"
                                                                onClick={() => handleRemoveFaixa(servicoId, idx)}
                                                                className="text-red-500 hover:text-red-700 text-xs font-medium px-2 py-1 rounded bg-red-50"
                                                            >
                                                                Remover faixa
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                            
                                            {isEditing && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleAddFaixa(servicoId)}
                                                    className="text-xs text-primary hover:text-primary-dark font-medium flex items-center mt-2 px-2 py-1 rounded hover:bg-blue-50 transition-colors"
                                                >
                                                    <Plus size={14} className="mr-1"/> Adicionar Faixa Extra
                                                </button>
                                            )}
                                        </div>

                                        <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 mt-5 pt-4 border-t border-gray-100">
                                            <label className="checkbox-label text-sm">
                                                <input
                                                    type="checkbox"
                                                    checked={servico.comissao.considerarTaxas || false}
                                                    onChange={(e) => handleComissaoChange(servicoId, 'considerarTaxas', e.target.checked)}
                                                    disabled={!isEditing}
                                                    className="form-checkbox"
                                                />
                                                <span className="checkbox-custom"></span>
                                                <span className="checkbox-label-text text-gray-700">Descontar Taxas do Cartão</span>
                                            </label>
                                            <label className="checkbox-label text-sm">
                                                <input
                                                    type="checkbox"
                                                    checked={servico.comissao.considerarDescontos !== false}
                                                    onChange={(e) => handleComissaoChange(servicoId, 'considerarDescontos', e.target.checked)}
                                                    disabled={!isEditing}
                                                    className="form-checkbox"
                                                />
                                                <span className="checkbox-custom"></span>
                                                <span className="checkbox-label-text text-gray-700">Descontar Promoções</span>
                                            </label>
                                        </div>
                                    </div>
                                )}
                            </div>

                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="space-y-4">
            {isEditing && (
                <div className="mt-2 relative px-2 flex gap-2">
                    <div className="relative flex-1">
                        <Search className="absolute left-5 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Buscar serviços por nome..."
                            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm shadow-sm"
                        />
                    </div>
                    <button 
                        type="button" 
                        onClick={handleReload} 
                        className="flex items-center justify-center text-sm font-medium text-gray-600 hover:text-gray-900 border border-gray-300 bg-white px-4 py-2.5 rounded-lg shadow-sm w-[130px]"
                    >
                        <RefreshCw size={16} className="mr-2" /> Atualizar
                    </button>
                </div>
            )}

            {servicosRenderizaveis.length === 0 ? (
                <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-200 mt-4 mx-2">
                    {searchTerm ? 'Nenhum serviço encontrado na busca.' : 'Nenhum serviço disponível nesta conta.'}
                </div>
            ) : (
                <div className="mt-4 border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                    <div className="divide-y divide-gray-100">
                        {/* Vinculados no topo */}
                        {servicosRenderizaveis.filter(s => s.isSelected).map(renderServicoRow)}
                        {/* Não vinculados em baixo, visíveis só editando */}
                        {isEditing && servicosRenderizaveis.filter(s => !s.isSelected).map(renderServicoRow)}
                    </div>
                </div>
            )}

            {showDesvincularModal && servicoParaDesvincular && (
                <DesvincularServicoModal
                    show={showDesvincularModal}
                    onClose={() => {
                        setShowDesvincularModal(false);
                        setServicoParaDesvincular(null);
                        // Reverte o toggle na tela se cancelou o modal
                        if (servicoParaDesvincular?.servico) {
                            const idStr = extractId(servicoParaDesvincular.servico);
                            if (!servicosSelecionados.includes(idStr)) {
                                setServicosSelecionados(prev => [...prev, idStr]);
                            }
                        }
                    }}
                    vinculo={servicoParaDesvincular}
                    userNome={userNome}
                    onSuccess={handleDesvincularSuccess}
                />
            )}
        </div>
    );
};

export default UserServicesTab;